import { useState, useCallback, useRef, useEffect } from 'react'
import {
  saveYouTubeUserTranslation,
  deleteYouTubeUserTranslation,
} from '@/services/youtube.service'

/**
 * Auto-save state for the user translation editor.
 *
 * - idle: No pending save; textarea is clean or initial state.
 * - saving: A PUT request is in-flight after the 5-second idle debounce fired.
 * - saved: The last save completed successfully (resets to 'idle' after 2 seconds).
 * - error: The save failed; a small inline error indicator is shown.
 */
export type TranslationSaveState = 'idle' | 'saving' | 'saved' | 'error'

/**
 * useYouTubeTranslationEdit — manages the edit state and 5-second auto-save for a
 * YouTube segment translation.
 *
 * Design decisions (Story 3.3):
 * - Mirror of useYouTubeTranscriptEdit — intentionally NOT TanStack Query useMutation.
 * - 5s idle debounce: timer resets on every onTextChange; fires save on idle pause.
 * - Auto-save is silent (no toast); indicated by a small inline spinner + aria-live region.
 * - onTextChange with empty string does NOT trigger auto-save (see anti-patterns in story).
 * - exitEditMode cancels any pending debounce to prevent stale saves for the previous segment.
 * - revert calls deleteYouTubeUserTranslation; callers should re-fetch after revert resolves.
 * - Source differences: source='youtube' → enterEditMode with empty seed (user writes from
 *   scratch); source='google_translate' | 'user' → pre-seeded with existing translation text.
 *
 * Usage:
 *   const translationEditHook = useYouTubeTranslationEdit()
 *   // In VideoPlayer, call translationEditHook.exitEditMode() when segment changes.
 */
export const useYouTubeTranslationEdit = (onSaveSuccess?: (segmentId: number) => void) => {
  const [editMode, setEditMode] = useState(false)
  const [editText, setEditText] = useState('')
  const [saveState, setSaveState] = useState<TranslationSaveState>('idle')
  // lastSavedText tracks what was last successfully persisted to the server.
  // Starts equal to the seed text passed into enterEditMode so the Save button
  // is disabled until the user actually changes something.
  const [lastSavedText, setLastSavedText] = useState('')

  // Debounce timer ref — stores the setTimeout ID for the 5-second auto-save.
  // Using useRef avoids stale closure issues and prevents React re-renders from resetting it.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // segmentId ref — stored so the auto-save closure always sees the current segment.
  const segmentIdRef = useRef<number | null>(null)

  // Stable ref for onSaveSuccess so the debounce closure never captures a stale version.
  const onSaveSuccessRef = useRef(onSaveSuccess)
  useEffect(() => { onSaveSuccessRef.current = onSaveSuccess }, [onSaveSuccess])

  // Cleanup: cancel any pending debounce timer when the component unmounts.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  /**
   * Open the edit textarea, pre-populated with initialText.
   * - source='youtube': callers should pass '' (empty) so the user writes from scratch.
   * - source='google_translate' | 'user': callers should pass the current translation text.
   * Resets saveState to idle.
   */
  const enterEditMode = useCallback((segmentId: number, initialText: string) => {
    segmentIdRef.current = segmentId
    setEditText(initialText)
    setLastSavedText(initialText)
    setEditMode(true)
    setSaveState('idle')
  }, [])

  /**
   * Close the edit textarea and cancel any pending debounce timer.
   * Called when the selected segment changes (VideoPlayer segment-change effect).
   * Resets all state so the next segment starts clean.
   */
  const exitEditMode = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    setEditMode(false)
    setEditText('')
    setLastSavedText('')
    setSaveState('idle')
    segmentIdRef.current = null
  }, [])

  /**
   * Handle textarea input change.
   * Updates editText and resets the 5-second debounce timer.
   * Does NOT trigger auto-save when text is empty (anti-pattern: saves empty string).
   */
  const onTextChange = useCallback((text: string) => {
    setEditText(text)

    // Cancel the current pending timer.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    // Do not auto-save empty text — treat it as a pre-revert state, not a valid edit.
    if (text.trim() === '') {
      return
    }

    // Schedule a new save after 5 seconds of idle.
    debounceTimerRef.current = setTimeout(async () => {
      debounceTimerRef.current = null
      const segmentId = segmentIdRef.current
      if (segmentId == null) return

      setSaveState('saving')
      try {
        await saveYouTubeUserTranslation(segmentId, text)
        setLastSavedText(text)
        setSaveState('saved')
        // Evict warm prefetch cache so returning to this segment fetches fresh DB text.
        onSaveSuccessRef.current?.(segmentId)
        // Reset to idle after 2 seconds (brief visual confirmation, then silent).
        setTimeout(() => setSaveState('idle'), 2000)
      } catch {
        setSaveState('error')
      }
    }, 5000)
  }, [])

  /**
   * Immediately save the current editText, cancelling any pending debounce.
   * Called when the user clicks the manual Save button.
   * No-ops when editText is empty or no segment is active.
   */
  const saveNow = useCallback(async () => {
    // Cancel any pending debounce — the manual save supersedes it.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    const segmentId = segmentIdRef.current
    const text = editText
    if (segmentId == null || text.trim() === '') return

    setSaveState('saving')
    try {
      await saveYouTubeUserTranslation(segmentId, text)
      setLastSavedText(text)
      setSaveState('saved')
      // Evict warm prefetch cache so returning to this segment fetches fresh DB text.
      onSaveSuccessRef.current?.(segmentId)
      setTimeout(() => setSaveState('idle'), 2000)
    } catch {
      setSaveState('error')
    }
  }, [editText])

  /**
   * Revert by soft-deleting the source='user' row (or discarding the unsaved edit
   * for source='youtube' where no user row exists yet).
   * After this call, callers should trigger a translation re-fetch so the panel re-renders.
   * Does not throw — errors are swallowed since the UI re-fetch will indicate state.
   */
  const revert = useCallback(async (segmentId: number) => {
    // Cancel any pending auto-save before reverting.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    setSaveState('idle')
    try {
      await deleteYouTubeUserTranslation(segmentId)
    } catch {
      // Idempotent: if no user row exists the DELETE is a no-op; swallow errors.
    }
    setEditMode(false)
    setEditText('')
  }, [])

  // True when the current editText differs from the last persisted text and is non-empty.
  // Used to enable/disable the manual Save button.
  const hasUnsavedChanges = editMode && editText.trim() !== '' && editText !== lastSavedText

  return {
    editText,
    editMode,
    saveState,
    hasUnsavedChanges,
    enterEditMode,
    exitEditMode,
    onTextChange,
    saveNow,
    revert,
  }
}
